// What still stands between a waiting job and Robert's calendar (Oct 27, 2026).
//
// Greg: "stage of job is important or the hard gates missing to get that job
// to schedule build for robert to add to schedule. our crews (risol) wants 5
// days notice as preferred. we just need to do our best to meet that."
//
// The road to Robert: install_meeting → (RM submits; the hard gates) →
// build_submitted → (Chad or Robert approves) → schedule_build → Robert books
// the date. This file reads where a job is on that road. The gates themselves
// come from buildSubmitGates.ts and buildPaymentPlan.ts, the same checks the
// Submit button and the database use — nothing is re-implemented here.

import {
  agreementSigned,
  colorsChosen,
  homeownerColorsAuthorized,
  insuranceScopeOnFile,
  scopeEntered,
  type SubmitGateDeal,
} from "./buildSubmitGates";
import { firstPaymentStatus, usd, type BuildPaymentPlan } from "./buildPaymentPlan";
import { addDaysISO } from "./weeklySpiffs";

/** Risol's preferred notice before a build, in days. A goal, not a gate. */
export const NOTICE_DAYS = 5;

/** Columns a deal read needs for buildReadiness(). */
export const READINESS_COLS =
  "id,stage,agreement_status,install_agreement_signed,shingle_color,drip_edge_color,color_addendum_required," +
  "color_addendum_signed,insurance_paperwork_uploaded,scope_document_url,is_retail,rcv_total,retail_contract_total," +
  "depreciation_total,deductible,pwi_code,upgrades,retail_profit,at_cost_retail,acv_credit,dep_credit,misc_credit," +
  "non_commissionable,acv_only,pre_build_amount_received,post_build_amount_received," +
  "pre_build_amount_in_flight,post_build_amount_in_flight," +
  // Oct 29, 2026 — the trade money calcScope reads (migration 20261029f). The
  // first payment never includes a trade (it is due at completion), but every
  // column list that feeds calcScope carries them so no reader sees half a deal.
  "trade_ho_total,trade_ho_floor,trade_margin_loss,trade_ho_lines";

export type ReadinessTone = "ready" | "waiting" | "submit" | "blocked";

export interface Readiness {
  tone: ReadinessTone;
  /** One line: where the job is. */
  headline: string;
  /** What's missing before it can be submitted (empty unless blocked). */
  missing: string[];
}

function agreementGap(status: unknown): string {
  switch (status) {
    case null:
    case undefined:
    case "":
    case "none":
    case "draft":
      return "Agreement not generated";
    case "pending_approval":
      return "Agreement waiting on approval";
    case "changes_requested":
    case "needs_revision":
      return "Agreement needs changes";
    default:
      return "Agreement sent, not signed";
  }
}

export function buildReadiness(deal: Record<string, unknown>, plan: BuildPaymentPlan | null): Readiness {
  const stage = String(deal.stage ?? "");
  if (stage === "schedule_build" || stage === "build_scheduled") {
    return { tone: "ready", headline: "Approved · Robert can book it", missing: [] };
  }
  if (stage === "build_submitted") {
    return { tone: "waiting", headline: "Submitted · waiting on Chad or Robert to approve", missing: [] };
  }

  const d = deal as unknown as SubmitGateDeal;
  const missing: string[] = [];
  if (!agreementSigned(d)) missing.push(agreementGap(deal.agreement_status));
  if (!homeownerColorsAuthorized(d)) missing.push(colorsChosen(d) ? "Color addendum not signed" : "Colors not picked");
  if (!insuranceScopeOnFile(d)) missing.push("Insurance scope not uploaded");
  if (!scopeEntered(d)) missing.push("Scope numbers not entered");
  const fp = firstPaymentStatus(deal);
  if (!fp.isCaptured && !plan) {
    missing.push(fp.owed != null ? `${fp.label === "50% deposit" ? "Deposit" : "First payment"} ${usd(fp.owed)} not in, no plan` : "First payment not in, no plan");
  }

  if (missing.length === 0) {
    return { tone: "submit", headline: "Gates met · RM can submit for build approval", missing };
  }
  return {
    tone: "blocked",
    headline: `${missing.length} gate${missing.length === 1 ? "" : "s"} before it can be submitted`,
    missing,
  };
}

/** The last day to book a build and still give Risol their notice. */
export const bookBy = (buildDay: string) => addDaysISO(buildDay, -NOTICE_DAYS);

/** Booking this day today gives Risol less than their notice. */
export const shortNotice = (day: string, today: string | null) => !!today && day < addDaysISO(today, NOTICE_DAYS);

/** The first weekday a build could go on and still give Risol their notice. */
export function firstNoticeDay(today: string): string {
  let d = addDaysISO(today, NOTICE_DAYS);
  for (let i = 0; i < 3; i++) {
    const w = new Date(`${d}T12:00:00`).getDay();
    if (w !== 0 && w !== 6) break;
    d = addDaysISO(d, 1);
  }
  return d;
}
